/**
 * Listening indicator for the PublicTranscription iframe.
 *
 * Modernized design — a single rounded pill that visually integrates the
 * audio-reactive equalizer, the "Listening …" wordmark, the active section
 * chip, and the pause/stop controls. Replaces the earlier cramped layout
 * with proper breathing room, refined typography, and softer color choices.
 *
 * Fits the 260px x 40px iframe slot reserved by EyePegasus's
 * ehr-under-tabbar component.
 */

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AnimatePresence, motion } from 'framer-motion';
import { Mic, Pause, Square, Volume2 } from 'lucide-react';

interface CompactListeningIndicatorProps {
  isRecording: boolean;
  isPaused: boolean;
  audioLevel: number;
  /** Natural-language label of the section currently being dictated, if any. */
  activeSectionKeyword?: string | null;
  /**
   * When true the pill switches into "Speaking…" mode while Deepgram
   * Aura TTS plays a history read-back. STT is paused in this state by
   * the parent so the equalizer wouldn't be meaningful anyway.
   */
  isSpeaking?: boolean;
  /** Optional short label describing what is being spoken (e.g. "External history"). */
  speakingLabel?: string | null;
  onStartRecording: () => void;
  onPauseRecording: () => void;
  onResumeRecording: () => void;
  onStopRecording: () => void;
  /**
   * Called when the user presses the stop button WHILE TTS is playing.
   * Should interrupt the playback only — the voice-scribe session
   * itself keeps running. Falls back to `onStopRecording` if omitted.
   */
  onStopSpeaking?: () => void;
}

// 5 bars. Each gets its own phase + amplitude so the equalizer reads as
// organic, not mechanical.
const BAR_CONFIG = [
  { baseHeight: 4, phase: 0.0, amp: 8 },
  { baseHeight: 6, phase: 0.18, amp: 12 },
  { baseHeight: 5, phase: 0.36, amp: 10 },
  { baseHeight: 7, phase: 0.54, amp: 13 },
  { baseHeight: 4, phase: 0.72, amp: 9 },
];

/**
 * Per-bar animation keyframes — computed ONCE at module load. The bug
 * we hit previously: folding audioLevel directly into these arrays
 * meant Framer Motion's `animate` prop got a new object every render
 * (audioLevel updates on every RAF tick, ~60 times a second), which
 * restarted the looped animation each time so the bars never actually
 * completed a cycle. By keeping keyframes stable, the loop runs
 * uninterrupted; audioLevel modulates *amplitude* via a wrapper
 * scaleY transform instead (smoothed by a CSS transition).
 */
const BAR_ANIMS = BAR_CONFIG.map((bar) => ({
  height: [
    bar.baseHeight,
    bar.baseHeight + bar.amp,
    bar.baseHeight + bar.amp * 0.4,
    bar.baseHeight + bar.amp * 0.85,
    bar.baseHeight,
  ],
  opacity: [0.7, 1, 0.85, 1, 0.7],
}));

const Equalizer = ({
  audioLevel,
  isPaused,
}: {
  audioLevel: number;
  isPaused: boolean;
}) => {
  // Map audio level to a smooth scaleY. Minimum 0.55 even at silence so
  // the equalizer reads as "alive, listening" rather than flat. Tops
  // out at ~1.1 so loud bursts visibly emphasize the bars.
  const scale = isPaused ? 0.45 : 0.55 + Math.min(1, audioLevel) * 0.55;
  return (
    <div
      aria-hidden
      className="flex items-end gap-[2.5px] h-[18px] flex-shrink-0"
      style={{
        width: 26,
        transform: `scaleY(${scale})`,
        transformOrigin: '50% 100%',
        transition: 'transform 120ms ease-out',
      }}>
      {BAR_CONFIG.map((bar, i) => (
        <motion.span
          key={i}
          className="w-[2.5px] rounded-full"
          style={{
            height: bar.baseHeight,
            background:
              'linear-gradient(180deg, #818cf8 0%, #a78bfa 60%, #f472b6 100%)',
          }}
          animate={
            isPaused ? { height: bar.baseHeight, opacity: 0.35 } : BAR_ANIMS[i]
          }
          transition={{
            // Slow, ambient breathing — bars cycle between 2.8s and
            // ~3.5s. Reads as a quiet "we're listening" rather than
            // an active VU meter.
            duration: 2.8 + bar.phase,
            repeat: Infinity,
            delay: bar.phase,
            ease: 'easeInOut',
          }}
        />
      ))}
    </div>
  );
};

export const CompactListeningIndicator = ({
  isRecording,
  isPaused,
  audioLevel,
  activeSectionKeyword,
  isSpeaking = false,
  speakingLabel,
  onStartRecording,
  onPauseRecording,
  onResumeRecording,
  onStopRecording,
  onStopSpeaking,
}: CompactListeningIndicatorProps) => {
  // The session may only be ended when no section is actively being
  // dictated. While a section is open (a START command fired and it
  // hasn't been finalized) the stop button is disabled so the clinician
  // can't tear down the session mid-section — they must close the
  // section first (e.g. say "Access Done"). Pause does NOT lift this:
  // a paused section is still an open section. TTS playback is exempt —
  // during playback the button acts as "Stop playback" (see below), so
  // the lock only applies when we're not speaking.
  const stopDisabled = !isSpeaking && !!activeSectionKeyword;

  // While TTS is playing the red square button reads as an "interrupt
  // playback" control rather than "end session". The voice-scribe
  // session keeps running; only the audio playback is halted.
  const handleStopClick = () => {
    if (stopDisabled) return;
    if (isSpeaking && onStopSpeaking) {
      onStopSpeaking();
      return;
    }
    onStopRecording();
  };
  const stopAriaLabel = isSpeaking ? 'Stop playback' : 'End Access session';
  const stopTooltip = stopDisabled
    ? 'Finish the current section first'
    : isSpeaking
    ? 'Stop playback'
    : 'End session';

  // Glow when actively capturing for a named section. Idle "Listening …"
  // stays subtle so the user can tell at a glance whether dictation is
  // being attributed to a section vs. just being heard. Paused and TTS
  // The spectrum-loop ring is the visual signal that transcription is
  // running. With the voice-driven section state machine removed (now
  // driven by per-test Generate buttons), there's no "active section"
  // concept any more — the ring glows whenever we're actively
  // capturing audio. Paused and Speaking states turn it off because
  // STT isn't running in either.
  const isSectionActive = isRecording && !isPaused && !isSpeaking;
  const pillShadow =
    '0 1px 2px rgba(99, 102, 241, 0.06), 0 2px 8px rgba(139, 92, 246, 0.08), inset 0 0 0 1px rgba(167, 139, 250, 0.25)';
  return (
    <div
      className="relative w-full h-full flex items-center justify-center px-2"
      style={{ top: '5px' }}>
      <AnimatePresence mode="wait">
        {
          !isRecording ?
            // Idle: a clean centered Mic button. The label "AI Scribe" is
            // outside the iframe (in the under-tabbar trigger button) so the
            // iframe shows nothing until recording is active.
            <motion.div
              key="start-view"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center justify-center w-full">
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <motion.button
                    onClick={onStartRecording}
                    aria-label="Start Access voice scribe"
                    className="relative flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center shadow-md transition-all duration-300 hover:shadow-lg hover:scale-105 active:scale-95 touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 overflow-hidden"
                    style={{
                      background:
                        'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
                    }}
                    whileTap={{ scale: [1, 0.92, 1.05, 1] }}
                    transition={{ duration: 0.3 }}>
                    <Mic className="w-[15px] h-[15px] text-white relative z-10" />
                  </motion.button>
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={6} className="z-50">
                  <p className="text-[12px] font-medium">Start Access</p>
                  <p className="text-[11px] opacity-75 mt-0.5">
                    Say "Access <em>section name</em>"
                  </p>
                </TooltipContent>
              </Tooltip>
            </motion.div>
            // Active: an integrated pill. Soft inner gradient + 1px violet
            // border. Equalizer, label, optional section badge, and the two
            // controls all share one continuous surface.
            // Wrapper holds the rotating spectrum ring + the pill core.
            // The ring sits behind the pill at inset:-2px so a 2px rim of
            // the rotating gradient is visible all the way around. The
            // pill itself retains overflow-hidden so the equalizer / label
            // / controls stay clipped to its rounded shape.
            //
            // Pill is h-8 (32px) — sized so that 32 + 2*2 (ring inset) =
            // 36px outer diameter fits inside the 40px iframe slot with
            // 2px clearance top and bottom (no clipping).
          : <motion.div
              key="listening-pill"
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.2 }}
              className="relative w-full h-8">
              {/* Rotating Spectrum Loop ring.
                ─ The element stays still; only the conic gradient's
                  `from` angle animates via the registered
                  --spectrum-angle custom property (see index.css).
                  Using transform: rotate() would have to spin the whole
                  ring element and could subpixel-blur the rim.
                ─ Kept mounted so the gradient animation keeps running;
                  Framer Motion only drives the opacity fade when
                  isSectionActive flips. */}
              <motion.div
                aria-hidden
                className="spectrum-loop-ring absolute pointer-events-none rounded-full"
                style={{ inset: -2 }}
                initial={{ opacity: 0 }}
                animate={{ opacity: isSectionActive ? 1 : 0 }}
                transition={{ duration: 0.35, ease: 'easeOut' }}
              />
              {/* Pill core. Punches out the middle of the ring so only the
                2px outer rim of the spinning gradient remains visible. */}
              <div
                className="relative flex items-center w-full h-full rounded-full overflow-hidden"
                style={{
                  // FULLY OPAQUE on purpose. Previously this was
                  // rgba(..., 0.95) — 5% transparency was enough for the
                  // rotating Spectrum Loop ring behind the pill to bleed
                  // through and visibly tint the inside, making it look
                  // like the interior was animating too. With alpha = 1
                  // the spinning gradient is visible ONLY in the 2px rim
                  // and the interior stays the static soft wash.
                  background:
                    'linear-gradient(135deg, rgb(238, 242, 255) 0%, rgb(250, 245, 255) 50%, rgb(253, 242, 248) 100%)',
                  boxShadow: pillShadow,
                }}>
                {/* Left: equalizer OR speaker icon (during TTS) */}
                <div className="flex items-center pl-3 pr-2 flex-shrink-0">
                  {isSpeaking ?
                    <motion.div
                      aria-hidden
                      className="flex items-center justify-center"
                      style={{ width: 26, height: 18 }}
                      animate={{
                        scale: [1, 1.15, 1],
                        opacity: [0.85, 1, 0.85],
                      }}
                      transition={{
                        duration: 1.4,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}>
                      <Volume2
                        className="w-[16px] h-[16px]"
                        style={{ color: '#8b5cf6' }}
                      />
                    </motion.div>
                  : <Equalizer audioLevel={audioLevel} isPaused={isPaused} />}
                </div>

                {/* Middle: label + active section / speaking label */}
                <div className="flex-1 min-w-0 flex items-center gap-1.5 pr-1">
                  <motion.span
                    className={`text-[12.5px] font-semibold tracking-tight ${
                      isPaused && !isSpeaking ?
                        'text-slate-500 dark:text-slate-400'
                      : 'bg-clip-text text-transparent'
                    }`}
                    style={
                      isPaused && !isSpeaking ? undefined : (
                        {
                          backgroundImage:
                            'linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899, #8b5cf6, #6366f1)',
                          backgroundSize: '200% 100%',
                        }
                      )
                    }
                    animate={
                      isPaused && !isSpeaking ?
                        { opacity: 1 }
                      : { backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }
                    }
                    transition={{
                      duration: 4.5,
                      repeat: isPaused && !isSpeaking ? 0 : Infinity,
                      ease: 'easeInOut',
                    }}>
                    {isSpeaking ?
                      'Speaking'
                    : isPaused ?
                      'Paused'
                    : 'Listening'}
                  </motion.span>

                  <AnimatePresence mode="wait">
                    {isSpeaking && speakingLabel && (
                      <motion.div
                        key={`speaking-${speakingLabel}`}
                        initial={{ opacity: 0, scale: 0.9, x: -4 }}
                        animate={{ opacity: 1, scale: 1, x: 0 }}
                        exit={{ opacity: 0, scale: 0.9, x: -4 }}
                        transition={{ duration: 0.2 }}
                        className="flex items-center gap-1 min-w-0 truncate">
                        <span className="text-slate-400 text-[12px] flex-shrink-0">
                          ·
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.05em] text-slate-600 dark:text-slate-300 truncate font-semibold leading-none">
                          {speakingLabel}
                        </span>
                      </motion.div>
                    )}

                    {/*
                      With voice section commands removed, the
                      "active section" badge has been retired. The
                      only badge that remains is the speaking label
                      above (used for TTS history read-back). Idle
                      and recording states show just the pulsing
                      ellipsis after "Listening" / "Paused".
                    */}
                    {!isSpeaking && !isPaused && (
                      <motion.span
                        key="dots"
                        aria-hidden
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        className="text-[12.5px] font-semibold text-violet-500 dark:text-violet-400 flex-shrink-0 leading-none">
                        <motion.span
                          animate={{ opacity: [0.3, 1, 0.3] }}
                          transition={{
                            duration: 1.4,
                            repeat: Infinity,
                            ease: 'easeInOut',
                          }}>
                          …
                        </motion.span>
                      </motion.span>
                    )}
                  </AnimatePresence>
                </div>

                {/* Right: controls — flush against the pill edge, tiny gap between them */}
                <div className="flex items-center gap-1 pr-1 flex-shrink-0">
                  <Tooltip delayDuration={300}>
                    <TooltipTrigger asChild>
                      <motion.button
                        onClick={
                          isPaused ? onResumeRecording : onPauseRecording
                        }
                        aria-label={isPaused ? 'Resume Access' : 'Pause Access'}
                        className="relative w-7 h-7 rounded-full flex items-center justify-center bg-white/70 hover:bg-white dark:bg-slate-800/70 dark:hover:bg-slate-800 border border-slate-200/70 dark:border-slate-700 transition-all duration-150 hover:scale-105 active:scale-95 touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-1"
                        whileTap={{ scale: 0.9 }}>
                        <AnimatePresence mode="wait">
                          {isPaused ?
                            <motion.div
                              key="mic"
                              initial={{ scale: 0.5, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              exit={{ scale: 0.5, opacity: 0 }}
                              transition={{ duration: 0.12 }}>
                              <Mic className="w-[13px] h-[13px] text-slate-700 dark:text-slate-200" />
                            </motion.div>
                          : <motion.div
                              key="pause"
                              initial={{ scale: 0.5, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              exit={{ scale: 0.5, opacity: 0 }}
                              transition={{ duration: 0.12 }}>
                              <Pause className="w-[12px] h-[12px] text-slate-700 dark:text-slate-200" />
                            </motion.div>
                          }
                        </AnimatePresence>
                      </motion.button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      sideOffset={6}
                      className="z-50">
                      <p className="text-[12px] font-medium">
                        {isPaused ? 'Resume' : 'Pause'}
                      </p>
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip delayDuration={300}>
                    <TooltipTrigger asChild>
                      <motion.button
                        onClick={handleStopClick}
                        aria-label={stopAriaLabel}
                        aria-disabled={stopDisabled}
                        className={`relative w-7 h-7 rounded-full flex items-center justify-center transition-all duration-150 touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 focus-visible:ring-offset-1 ${
                          stopDisabled
                            ? 'cursor-not-allowed opacity-40 grayscale'
                            : 'hover:scale-105 active:scale-95'
                        }`}
                        style={{
                          background:
                            'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)',
                          boxShadow:
                            '0 1px 3px rgba(244, 63, 94, 0.35), inset 0 -1px 0 rgba(0,0,0,0.1)',
                        }}
                        whileTap={stopDisabled ? undefined : { scale: 0.9 }}>
                        <Square
                          className="w-[10px] h-[10px] text-white relative z-10"
                          fill="white"
                          strokeWidth={0}
                        />
                      </motion.button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      sideOffset={6}
                      className="z-50">
                      <p className="text-[12px] font-medium">{stopTooltip}</p>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </motion.div>

        }
      </AnimatePresence>
    </div>
  );
};
